import { useCallback } from 'react';

import { SERIES_COLORS, STATUS_COLORS } from '../lib/chart-palette';
import { useAdminData } from '../lib/use-admin-data';
import {
  fetchAdminOverview,
  fetchAdminRoutes,
  fetchAdminTraffic,
  type AdminFilters,
  type AdminOverview,
  type RouteStat,
  type TrafficPoint,
} from '../services/api';
import { BreakdownBars } from './BreakdownBars';
import { Feedback } from './Feedback';
import { StatTile } from './StatTile';
import { TimeSeriesChart } from './TimeSeriesChart';

/**
 * Aba TRÁFEGO — o histórico do período escolhido, vindo do banco.
 *
 * Complementa a aba "ao vivo": lá se vê o agora com resolução de segundo;
 * aqui se vê a forma da curva ao longo de horas ou dias, que é o que revela
 * se o pico foi um evento isolado ou um degrau permanente.
 */

interface AdminTrafficTabProps {
  filters: AdminFilters;
}

export function AdminTrafficTab({ filters }: AdminTrafficTabProps) {
  const overviewLoader = useCallback(() => fetchAdminOverview(filters), [filters]);
  const trafficLoader = useCallback(() => fetchAdminTraffic(filters), [filters]);
  const routesLoader = useCallback(() => fetchAdminRoutes(filters), [filters]);

  const overview = useAdminData<AdminOverview>(overviewLoader);
  const traffic = useAdminData<{ bucketSeconds: number; series: TrafficPoint[] }>(trafficLoader);
  const routes = useAdminData<{ routes: RouteStat[] }>(routesLoader);

  const error = overview.error ?? traffic.error ?? routes.error;
  if (error) return <Feedback kind="error">{error}</Feedback>;
  if (!overview.data || !traffic.data) return <p className="page-loading">Carregando…</p>;

  const period = overview.data.period;
  const labels = traffic.data.series.map((point) => point.time);

  return (
    <div className="admin-tab">
      <div className="stat-grid">
        <StatTile
          hero
          label="Requisições no período"
          value={period.requests.toLocaleString('pt-BR')}
          detail={`${period.visitors.toLocaleString('pt-BR')} endereços diferentes`}
        />
        <StatTile
          label="Erros"
          value={period.errors.toLocaleString('pt-BR')}
          tone={period.serverErrors > 0 ? 'critical' : 'good'}
          detail={`${period.serverErrors.toLocaleString('pt-BR')} de servidor (5xx)`}
        />
        <StatTile
          label="Latência p95"
          value={`${period.p95Ms} ms`}
          detail={`p99 ${period.p99Ms} ms`}
        />
        <StatTile
          label="Latência mediana"
          value={`${period.p50Ms} ms`}
          detail={`média ${period.avgMs} ms`}
        />
        <StatTile
          label="Tráfego do teste de carga"
          value={period.syntheticRequests.toLocaleString('pt-BR')}
          detail="requisições marcadas como sintéticas"
        />
        <StatTile
          label="Robôs e ferramentas"
          value={period.botRequests.toLocaleString('pt-BR')}
          detail="buscadores, prévias de link, clientes HTTP"
        />
      </div>

      <TimeSeriesChart
        title="Requisições ao longo do tempo"
        description={`Cada ponto agrega ${formatBucket(traffic.data.bucketSeconds)}.`}
        labels={labels}
        filled
        series={[
          {
            key: 'total',
            label: 'Requisições',
            color: SERIES_COLORS[0],
            values: traffic.data.series.map((point) => point.total),
          },
        ]}
      />

      <TimeSeriesChart
        title="Respostas por classe de status"
        labels={labels}
        series={[
          {
            key: 'ok',
            label: 'Sucesso (2xx/3xx)',
            color: STATUS_COLORS.good,
            values: traffic.data.series.map((point) => point.ok),
          },
          {
            key: 'client',
            label: 'Erro do cliente (4xx)',
            color: STATUS_COLORS.warning,
            values: traffic.data.series.map((point) => point.clientError),
          },
          {
            key: 'server',
            label: 'Erro do servidor (5xx)',
            color: STATUS_COLORS.critical,
            values: traffic.data.series.map((point) => point.serverError),
          },
        ]}
      />

      <TimeSeriesChart
        title="Latência por percentil"
        description="O p99 é a experiência do usuário mais azarado — é ele que reclama."
        labels={labels}
        formatValue={(value) => `${Math.round(value)} ms`}
        series={[
          {
            key: 'p50',
            label: 'p50 (mediana)',
            color: SERIES_COLORS[0],
            values: traffic.data.series.map((point) => point.p50Ms ?? 0),
          },
          {
            key: 'p95',
            label: 'p95',
            color: SERIES_COLORS[1],
            values: traffic.data.series.map((point) => point.p95Ms ?? 0),
          },
          {
            key: 'p99',
            label: 'p99',
            color: SERIES_COLORS[2],
            values: traffic.data.series.map((point) => point.p99Ms ?? 0),
          },
        ]}
      />

      <BreakdownBars
        title="Rotas mais chamadas"
        items={(routes.data?.routes ?? []).map((route) => ({
          label: `${route.method} ${route.route}`,
          value: route.requests,
          detail: `${formatVisitorCount(route.visitors)} · p95 ${route.p95Ms} ms`,
        }))}
      />

      <section className="admin-section">
        <h3 className="admin-section__title">Custo e saúde por rota</h3>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Rota</th>
                <th scope="col">Requisições</th>
                <th scope="col">Erros</th>
                <th scope="col">p50</th>
                <th scope="col">p95</th>
                <th scope="col">p99</th>
                <th scope="col">Pior</th>
              </tr>
            </thead>
            <tbody>
              {(routes.data?.routes ?? []).map((route) => (
                <tr key={`${route.method} ${route.route}`}>
                  <td>
                    <code>
                      {route.method} {route.route}
                    </code>
                  </td>
                  <td className="data-table__number">{route.requests.toLocaleString('pt-BR')}</td>
                  <td className="data-table__number">
                    {route.errors > 0 ? (
                      <span className="data-table__alert">
                        {route.errors.toLocaleString('pt-BR')} ({(route.errorRate * 100).toFixed(1)}
                        %)
                      </span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="data-table__number">{route.p50Ms} ms</td>
                  <td className="data-table__number">{route.p95Ms} ms</td>
                  <td className="data-table__number">{route.p99Ms} ms</td>
                  <td className="data-table__number">{route.maxMs} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

/** "1 visitante" / "2 visitantes". */
function formatVisitorCount(total: number): string {
  return `${total.toLocaleString('pt-BR')} ${total === 1 ? 'visitante' : 'visitantes'}`;
}

/** 1800 → "30 min"; 120 → "2 min"; 30 → "30s" */
function formatBucket(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${seconds / 60} min`;
  return `${seconds / 3600} h`;
}
